import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Info } from "lucide-react";
import Select from "react-select";
import { ICooperative, INewNotice } from "../../exports/interfaces";
import CooperativeSelectionDrawer from "./CooperativeSelectionDrawer";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import useMobile from "../../hooks/useMobile";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useOption } from "../../context/OptionContext";
import {
  convertNepaliToEnglishDate,
  formatDateBySetting,
  getCooperativeDisplayName,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import NepaliCalendar from "../NepaliCalendar";
import { useSettings } from "../../context/SettingsContext";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const VALID_COOPERATIVE_STATUSES = ["Active", "Approved", "Merged"];

const NewNotice = ({ cooperative }: { cooperative: ICooperative[] }) => {
  const isMobile = useMobile();

  const validCooperatives = useMemo(
    () =>
      (cooperative || []).filter((c) =>
        VALID_COOPERATIVE_STATUSES.includes(c.status),
      ),
    [cooperative],
  );
  const { categories } = useOption();
  const { setLoading } = useLoader();
  const { generalSettings } = useSettings();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const navigate = useNavigate();
  const publishDateDropdownRef = useRef<HTMLDivElement>(null);
  const endDateDropdownRef = useRef<HTMLDivElement>(null);
  const [showPublishCalendar, setShowPublishCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [showCoopDrawer, setShowCoopDrawer] = useState(false);
  const [selectedCoopIds, setSelectedCoopIds] = useState<Set<number>>(
    new Set(),
  );
  const [formData, setFormData] = useState<INewNotice>({
    noticeType: "",
    noticeTo: "",
    cooperativeName: "",
    noticeTitle: "",
    noticeDetails: "",
    publishDate: "",
    endDate: "",
    fileName: "",
    file: null,
    status: "Active",
  });
  const [errors, setErrors] = useState({
    noticeType: "",
    noticeTo: "",
    cooperativeName: "",
    noticeTitle: "",
    noticeDetails: "",
    publishDate: "",
    endDate: "",
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        publishDateDropdownRef.current &&
        !publishDateDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPublishCalendar(false);
      }
      if (
        endDateDropdownRef.current &&
        !endDateDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEndCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!validateFileSize(file)) {
      e.target.value = "";
      return;
    }

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            fileName: file.name,
            file,
          }
        : prev,
    );
  };
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getNoticeErrors = (data: INewNotice) => {
    const validationErrors = {
      noticeType: "",
      noticeTo: "",
      cooperativeName: "",
      noticeTitle: "",
      noticeDetails: "",
      publishDate: "",
      endDate: "",
    };

    if (!data.noticeType?.trim()) {
      validationErrors.noticeType = "Notice Type is required.";
    }

    if (!data.noticeTo?.trim()) {
      validationErrors.noticeTo = "Notice To is required.";
    }

    const hasCoop =
      typeof data?.cooperativeName === "object" &&
      data?.cooperativeName !== null
        ? Object.keys(data.cooperativeName).length > 0
        : Boolean(
            data?.cooperativeName?.trim() && data.cooperativeName !== "{}",
          );

    if (!hasCoop) {
      validationErrors.cooperativeName = "Cooperative Name is required.";
    }

    if (!data.noticeTitle?.trim()) {
      validationErrors.noticeTitle = "Notice Title is required.";
    }

    if (!data.publishDate?.trim()) {
      validationErrors.publishDate = "Notice Publish Date is required.";
    }

    if (data.publishDate?.trim() && data.endDate?.trim()) {
      const start = new Date(data.publishDate);
      const end = new Date(data.endDate);
      if (
        !isNaN(start.getTime()) &&
        !isNaN(end.getTime()) &&
        end.getTime() < start.getTime()
      ) {
        validationErrors.publishDate =
          "Notice Publish Date cannot be after Notice End Date.";
        validationErrors.endDate =
          "Notice End Date cannot be before Notice Publish Date.";
      }
    }

    return validationErrors;
  };

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "noticeType",
      "noticeTo",
      "cooperativeName",
      "noticeTitle",
      "noticeDetails",
      "publishDate",
      "endDate",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    const element =
      firstErrorField === "noticeType" ||
      firstErrorField === "noticeTo" ||
      firstErrorField === "publishDate" ||
      firstErrorField === "endDate"
        ? (document.querySelector(
            `[data-field="${firstErrorField}"]`,
          ) as HTMLElement | null)
        : (document.querySelector(
            `[name="${firstErrorField}"]`,
          ) as HTMLElement | null);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const validateNoticeFields = (data: INewNotice) => {
    const validationErrors = getNoticeErrors(data);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error !== "")) {
      scrollToFirstError(validationErrors);
      return false;
    }

    return true;
  };

  const noticeTypeOptions =
    categories
      ?.find((item) => item.name === "Notice Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const categoryNoticeToOptions =
    categories
      ?.find((item) => item.name === "Nature of Work / Notice Audience")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const noticeToOptions = [
    { value: "All Cooperative", label: "All Cooperative" },
    { value: "Specific Cooperative", label: "Specific Cooperative" },
    ...categoryNoticeToOptions,
  ];

  // Update cooperativeName whenever selectedCoopIds changes
  useEffect(() => {
    if (formData.noticeTo === "All Cooperative") {
      const allObj: Record<number, string> = {};
      validCooperatives.forEach((c) => {
        allObj[c.id] = c.nameEnglish;
      });
      setFormData((prev) => ({
        ...prev,
        cooperativeName: JSON.stringify(allObj),
      }));
    } else {
      const isCategory =
        formData.noticeTo &&
        formData.noticeTo !== "All Cooperative" &&
        formData.noticeTo !== "Specific Cooperative";

      const selectedObj: Record<number, string> = {};
      validCooperatives
        .filter(
          (c) =>
            selectedCoopIds.has(c.id) &&
            (!isCategory || c.natureOfWork === formData.noticeTo),
        )
        .forEach((c) => {
          selectedObj[c.id] = c.nameEnglish;
        });

      setFormData((prev) => ({
        ...prev,
        cooperativeName:
          Object.keys(selectedObj).length > 0
            ? JSON.stringify(selectedObj)
            : "",
      }));
    }
  }, [selectedCoopIds, formData.noticeTo, validCooperatives]);

  const handleSelectChange = (
    name: keyof INewNotice,
    selected: SelectOption | null,
  ) => {
    const value = selected?.value || "";
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : prev,
    );

    // Handle noticeTo-specific logic
    if (name === "noticeTo") {
      if (value === "All Cooperative") {
        // Select all cooperatives
        setSelectedCoopIds(new Set(validCooperatives.map((c) => c.id)));
      } else if (value === "Specific Cooperative") {
        setSelectedCoopIds(new Set());
        setShowCoopDrawer(true);
      } else if (value) {
        // Category-based: pre-select cooperatives matching this natureOfWork
        const matching = validCooperatives
          .filter((c) => c.natureOfWork === value)
          .map((c) => c.id);
        setSelectedCoopIds(new Set(matching));
        setShowCoopDrawer(true);
      } else {
        setSelectedCoopIds(new Set());
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateNoticeFields(formData)) {
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();

      payload.append("noticeType", formData.noticeType);
      payload.append("noticeTo", formData.noticeTo);
      const coopPayload =
        typeof formData.cooperativeName === "string"
          ? formData.cooperativeName
          : JSON.stringify(formData.cooperativeName || {});
      payload.append("cooperativeName", coopPayload);
      payload.append("noticeTitle", formData.noticeTitle);
      payload.append("noticeDetails", formData.noticeDetails);
      payload.append("publishDate", formData.publishDate);
      payload.append("endDate", formData.endDate);

      if (formData.file) {
        payload.append("documentUrl", formData.file);
      }

      const response = await axiosClient.post("/notices", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200 || response.status === 201) {
        setFormData({
          noticeType: "",
          noticeTo: "",
          cooperativeName: "",
          noticeTitle: "",
          noticeDetails: "",
          publishDate: "",
          endDate: "",
          fileName: "",
          file: null,
          status: "Active",
        });
        sendNotification(`Notice sent`, user?.user?.userName || "User");
        dynamicSuccess("Notice sent successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Create New Notice</h3>
          <small>
            Record a new notice from a cooperative member or stakeholder
          </small>
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3" style={{ overflow: "unset" }}>
        <h5 className="text-uppercase mb-4">Notice Details</h5>

        <div className="row g-3">
          <div className="col-md-4" data-field="noticeType">
            <label className="form-label">
              Notice Type <span className="primary-color">*</span>
            </label>
            <SelectWithError
              styles={customSelectStyles}
              isError={Boolean(errors.noticeType)}
              options={noticeTypeOptions}
              placeholder="Select Notice Type"
              value={
                noticeTypeOptions.find(
                  (option) => option.value === formData?.noticeType,
                ) || null
              }
              onChange={(selected: SelectOption | null) =>
                handleSelectChange("noticeType", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.noticeType && (
              <p className="error-message">{errors.noticeType}</p>
            )}
          </div>
          <div className="col-md-4" data-field="noticeTo">
            <label className="form-label">
              Notice To <span className="primary-color">*</span>
            </label>
            <SelectWithError
              styles={customSelectStyles}
              isError={Boolean(errors.noticeTo)}
              options={noticeToOptions}
              placeholder="Select Notice To"
              value={
                noticeToOptions.find(
                  (option) => option.value === formData?.noticeTo,
                ) || null
              }
              onChange={(selected: SelectOption | null) =>
                handleSelectChange("noticeTo", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.noticeTo && (
              <p className="error-message">{errors.noticeTo}</p>
            )}
          </div>
          {!isMobile && <div className="col-md-4"></div>}
          <div className="col-md-8">
            <label className="form-label">
              {" "}
              Cooperative Name <span className="primary-color">*</span>
            </label>
            <div className="position-relative">
              <input
                className={`form-control ${errors.cooperativeName ? "input-error" : ""}`}
                type="text"
                name="cooperativeName"
                value={getCooperativeDisplayName(formData.cooperativeName)}
                readOnly
                placeholder={
                  formData.noticeTo === "All Cooperative"
                    ? "All cooperatives selected"
                    : "Select cooperatives"
                }
                style={{
                  paddingRight:
                    formData.noticeTo && formData.noticeTo !== "All Cooperative"
                      ? "38px"
                      : undefined,
                }}
              />
              {formData.noticeTo && formData.noticeTo !== "All Cooperative" && (
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent position-absolute"
                  style={{
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--primary-color)",
                  }}
                  onClick={() => setShowCoopDrawer(true)}
                  title="Select Cooperatives"
                >
                  <Info size={18} />
                </button>
              )}
            </div>
            {errors.cooperativeName && (
              <p className="error-message">{errors.cooperativeName}</p>
            )}
          </div>

          {!isMobile && <div className="col-md-4"></div>}
          <div className="col-md-8">
            <label className="form-label">
              {" "}
              Notice Title <span className="primary-color">*</span>
            </label>
            <input
              className={`form-control ${errors.noticeTitle ? "input-error" : ""}`}
              type="text"
              name="noticeTitle"
              value={formData.noticeTitle}
              onChange={handleChange}
              placeholder="Enter Notice Title"
            />
            {errors.noticeTitle && (
              <p className="error-message">{errors.noticeTitle}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">Notice Details</label>
            <textarea
              className="form-control"
              rows={5}
              name="noticeDetails"
              value={formData.noticeDetails}
              onChange={handleChange}
              placeholder="Enter Notice Details"
            />
          </div>
          <div className="col-12">
            <label className="form-label">
              Notice Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${!formData.file && "text-center"}`}
            >
              {formData.file ? (
                renderFilePreview(formData.file, formData.fileName, (e: any) =>
                  handleFileChange(e),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
          <div className="col-md-3" data-field="publishDate">
            <label className="form-label">
              Notice Publish Date <span className="primary-color">*</span>
            </label>
            <div
              className="dropdown position-relative"
              ref={publishDateDropdownRef}
            >
              <button
                type="button"
                className={`form-control text-start nepali-date-picker ${
                  errors.publishDate ? "input-error" : ""
                }`}
                onClick={() => setShowPublishCalendar(!showPublishCalendar)}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span className="text-truncate">
                    {formatDateBySetting(
                      formData.publishDate,
                      generalSettings?.dateFormat,
                    ) ||
                      generalSettings?.dateFormat ||
                      "mm/dd/yyyy"}
                  </span>

                  <i className="fa-regular fa-calendar"></i>
                </div>
              </button>

              {showPublishCalendar && (
                <div
                  className="dropdown-menu show p-0"
                  style={{
                    width: isMobile ? "249px" : "560px",
                    height: isMobile ? "407px" : "380px",
                    border: "1px solid #ddd",
                    zIndex: 1050,
                    bottom: "100%",
                    top: "auto",
                    marginBottom: "4px",
                  }}
                >
                  <NepaliCalendar
                    onDateSelect={(nepaliDate, englishDate) => {
                      const convertedEnglishDate = convertNepaliToEnglishDate(
                        nepaliDate,
                        englishDate,
                      );

                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const selected = new Date(convertedEnglishDate);
                      if (selected.getTime() < today.getTime()) {
                        setErrors((prev) => ({
                          ...prev,
                          publishDate:
                            "Notice Publish Date cannot be before today.",
                        }));
                        setShowPublishCalendar(false);
                        return;
                      }

                      if (
                        formData.endDate &&
                        new Date(convertedEnglishDate).getTime() >
                          new Date(formData.endDate).getTime()
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          publishDate:
                            "Notice Publish Date cannot be after Notice End Date.",
                        }));
                        setShowPublishCalendar(false);
                        return;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        publishDate: convertedEnglishDate,
                      }));
                      setErrors((prev) => {
                        const updated = { ...prev, publishDate: "" };
                        if (
                          prev.endDate ===
                          "Notice End Date cannot be before Notice Publish Date."
                        ) {
                          updated.endDate = "";
                        }
                        return updated;
                      });
                      setShowPublishCalendar(false);
                    }}
                  />
                </div>
              )}
            </div>
            {errors.publishDate && (
              <p className="error-message">{errors.publishDate}</p>
            )}
          </div>
          {!isMobile && (
            <>
              <div className="col-md-3"></div> <div className="col-md-3"></div>
            </>
          )}

          <div className="col-md-3" data-field="endDate">
            <label className="form-label">Notice End Date</label>
            <div
              className="dropdown position-relative"
              ref={endDateDropdownRef}
            >
              <button
                type="button"
                className={`form-control text-start nepali-date-picker ${
                  errors.endDate ? "input-error" : ""
                }`}
                onClick={() => setShowEndCalendar(!showEndCalendar)}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span className="text-truncate">
                    {formatDateBySetting(
                      formData.endDate,
                      generalSettings?.dateFormat,
                    ) ||
                      generalSettings?.dateFormat ||
                      "mm/dd/yyyy"}
                  </span>

                  <i className="fa-regular fa-calendar"></i>
                </div>
              </button>

              {showEndCalendar && (
                <div
                  className="dropdown-menu dropdown-menu-end show p-0"
                  style={{
                    width: isMobile ? "249px" : "560px",
                    height: isMobile ? "407px" : "380px",
                    border: "1px solid #ddd",
                    zIndex: 1050,
                    bottom: "100%",
                    top: "auto",
                    marginBottom: "4px",
                    right: 0,
                    left: "auto",
                  }}
                >
                  <NepaliCalendar
                    onDateSelect={(nepaliDate, englishDate) => {
                      const convertedEnglishDate = convertNepaliToEnglishDate(
                        nepaliDate,
                        englishDate,
                      );

                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const selected = new Date(convertedEnglishDate);
                      if (selected.getTime() < today.getTime()) {
                        setErrors((prev) => ({
                          ...prev,
                          endDate: "Notice End Date cannot be before today.",
                        }));
                        setShowEndCalendar(false);
                        return;
                      }

                      if (
                        formData.publishDate &&
                        new Date(convertedEnglishDate).getTime() <
                          new Date(formData.publishDate).getTime()
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          endDate:
                            "Notice End Date cannot be before Notice Publish Date.",
                        }));
                        setShowEndCalendar(false);
                        return;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        endDate: convertedEnglishDate,
                      }));
                      setErrors((prev) => {
                        const updated = { ...prev, endDate: "" };
                        if (
                          prev.publishDate ===
                          "Notice Publish Date cannot be after Notice End Date."
                        ) {
                          updated.publishDate = "";
                        }
                        return updated;
                      });
                      setShowEndCalendar(false);
                    }}
                  />
                </div>
              )}
            </div>
            {errors.endDate && (
              <p className="error-message">{errors.endDate}</p>
            )}
          </div>
        </div>

        <div className="text-end mt-3">
          <button
            className="btn btn-badge-border px-3 me-2"
            onClick={() => navigate("/notices")}
          >
            Cancel
          </button>
          <button className="btn btn-badge-primary px-3" onClick={handleSubmit}>
            Send Notice
          </button>
        </div>
      </div>

      {/* <div className="text-end mt-3">
           <button
             className="btn btn-badge-primary px-3"
             onClick={() => navigate("/complaints-list")}
           >
             Back to list
           </button>
         </div> */}
      <CooperativeSelectionDrawer
        open={showCoopDrawer}
        onClose={() => setShowCoopDrawer(false)}
        cooperatives={validCooperatives}
        selectedCoopIds={selectedCoopIds}
        setSelectedCoopIds={setSelectedCoopIds}
        noticeTo={formData.noticeTo}
      />
    </>
  );
};

export default NewNotice;
